import { ref, readonly } from 'vue'
import type { Session, Student } from '@/types/global'
import type { SessionCallConnection, SessionCallParticipant } from '@/services/session-calls'

const showVideoCallModal = ref(false)
const videoCallSession = ref<Session | null>(null)
const videoCallStudent = ref<Student | null>(null)
const videoCallId = ref<number | null>(null)
const videoCallAgoraUID = ref<string | undefined>()
const videoCallParticipants = ref<SessionCallParticipant[]>([])
const videoCallRoomType = ref<'private' | 'group' | undefined>()

export function useVideoCall() {
  function joinVideoCall(session: Session) {
    videoCallSession.value = session
    videoCallStudent.value = null
    videoCallId.value = null
    videoCallAgoraUID.value = undefined
    videoCallParticipants.value = []
    videoCallRoomType.value = undefined
    showVideoCallModal.value = true
  }

  function joinStudentVideoCall(student: Student, session: Session) {
    // For now, student call uses the same session room/token flow.
    videoCallSession.value = session
    videoCallStudent.value = student
    videoCallId.value = null
    videoCallAgoraUID.value = undefined
    videoCallParticipants.value = []
    videoCallRoomType.value = undefined
    showVideoCallModal.value = true
  }

  function joinPrivateStudentCall(
    student: Student,
    session: Session,
    connection: SessionCallConnection,
    participants: SessionCallParticipant[]
  ) {
    videoCallSession.value = {
      ...session,
      agora_room: connection.agoraRoom,
      agora_token: connection.agoraToken
    }
    videoCallStudent.value = student
    videoCallId.value = connection.callId
    videoCallAgoraUID.value = connection.agoraUid
    videoCallParticipants.value = participants
    videoCallRoomType.value = 'private'
    showVideoCallModal.value = true
  }

  function joinSessionRoomCall(
    session: Session,
    connection: SessionCallConnection,
    participants: SessionCallParticipant[]
  ) {
    videoCallSession.value = {
      ...session,
      agora_room: connection.agoraRoom,
      agora_token: connection.agoraToken
    }
    videoCallStudent.value = null
    videoCallId.value = connection.callId
    videoCallAgoraUID.value = connection.agoraUid
    videoCallParticipants.value = participants
    videoCallRoomType.value = 'group'
    showVideoCallModal.value = true
  }

  function closeVideoCall() {
    showVideoCallModal.value = false
    videoCallSession.value = null
    videoCallStudent.value = null
    videoCallId.value = null
    videoCallAgoraUID.value = undefined
    videoCallParticipants.value = []
    videoCallRoomType.value = undefined
  }

  return {
    showVideoCallModal: readonly(showVideoCallModal),
    videoCallSession: readonly(videoCallSession),
    videoCallStudent: readonly(videoCallStudent),
    videoCallId: readonly(videoCallId),
    videoCallAgoraUID: readonly(videoCallAgoraUID),
    videoCallParticipants: readonly(videoCallParticipants),
    videoCallRoomType: readonly(videoCallRoomType),
    joinVideoCall,
    joinStudentVideoCall,
    joinPrivateStudentCall,
    joinSessionRoomCall,
    closeVideoCall,
    // For App.vue to bind to the modal
    _showVideoCallModal: showVideoCallModal,
    _videoCallSession: videoCallSession,
    _videoCallStudent: videoCallStudent,
    _videoCallId: videoCallId,
    _videoCallAgoraUID: videoCallAgoraUID,
    _videoCallParticipants: videoCallParticipants,
    _videoCallRoomType: videoCallRoomType
  }
}
